import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { getTaskByIdApi } from "../features/scrap/scrapeAPI";
import { deleteActionApi } from "../features/tasks/taskAPI";
import LoadingComponent from "../components/common/LoadingComponent";
import { confirmationModal } from "../constants/confirmation.constants";
import { formatDate } from "./Meetings/Meetings.utils";
import {
  TASK_CATEGORY,
  CATEGORY_CONFIG,
  STATUS_BADGE,
  SCRAPPER_PLATFORMS,
} from "../constants/constants";

const getSourceMeta = (url = "") => {
  if (url.includes("facebook.com")) {
    return {
      label: SCRAPPER_PLATFORMS?.FACEBOOK,
      icon: "bi-facebook",
      color: "#1877f2",
    };
  }
  if (url.includes("youtube.com") || url.includes("youtu.be")) {
    return {
      label: SCRAPPER_PLATFORMS?.YOUTUBE,
      icon: "bi-youtube",
      color: "#ff0000",
    };
  }
  if (url.includes("instagram.com")) {
    return {
      label: SCRAPPER_PLATFORMS?.INSTAGRAM,
      icon: "bi-instagram",
      color: "#E4405F",
    };
  }
  return { label: "Social Media", icon: "bi-globe", color: "#6c757d" };
};

const getActionSourceUrl = (platformLabel, sourceMessageId, title = "") => {
  const id = String(sourceMessageId || "").trim();
  if (!id) return null;

  if (platformLabel === SCRAPPER_PLATFORMS?.FACEBOOK) {
    if (/^\d+$/.test(id)) {
      return `https://www.facebook.com/reel/${id}`;
    }

    const pageUrl = String(title).match(
      /https?:\/\/(?:www\.)?facebook\.com\/[^/\s)\]]+/,
    )?.[0];

    return pageUrl ? `${pageUrl}/posts/${id}` : null;
  }

  if (platformLabel === SCRAPPER_PLATFORMS?.YOUTUBE) {
    return `https://www.youtube.com/watch?v=${id}`;
  }

  if (platformLabel === SCRAPPER_PLATFORMS?.INSTAGRAM) {
    return `https://www.instagram.com/p/${sourceMessageId}`;
  }

  return null;
};

function SocialMediaScrapingDetail() {
  const { taskId } = useParams();
  // const navigate = useNavigate();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTask = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const data = await getTaskByIdApi(taskId);
      setTask(data);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to load task.",
      );
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    fetchTask();
  }, [fetchTask]);

  const sourceMeta = getSourceMeta(task?.title);
  const badge = STATUS_BADGE[task?.status] || STATUS_BADGE.PENDING;
  const actions = task?.actions || [];

  const actionCount = actions?.length ?? 0;

  const categorizedActions = useMemo(() => {
    const groups = {
      [TASK_CATEGORY.COMPLAINT]: [],
      [TASK_CATEGORY.LEAD]: [],
      [TASK_CATEGORY.QUERY]: [],
      [TASK_CATEGORY.NOISE]: [],
      other: [],
    };

    (task?.actions || []).forEach((action) => {
      const cat = (action.category || "").toLowerCase().trim();
      if (groups[cat]) {
        groups[cat].push(action);
      } else {
        groups.other.push(action);
      }
    });

    return groups;
  }, [task?.actions]);

  // Categories + "Other" (only when it has items) as one list for the table
  const sections = useMemo(() => {
    const list = CATEGORY_CONFIG.map((cat) => ({
      ...cat,
      items: categorizedActions[cat.key] || [],
    }));

    if (categorizedActions.other.length > 0) {
      list.push({
        key: "other",
        label: "Other",
        pluralLabel: "Other / Uncategorized",
        icon: "bi-tags-fill",
        color: "#6c757d",
        headerBg: undefined,
        badgeClass: "bg-secondary-subtle text-secondary-emphasis",
        items: categorizedActions.other,
      });
    }
    return list;
  }, [categorizedActions]);

  const [collapsedCategories, setCollapsedCategories] = useState({});

  const toggleCategory = (catKey) => {
    setCollapsedCategories((prev) => ({
      ...prev,
      [catKey]: !prev[catKey],
    }));
  };

  const handleCollapseAll = () => {
    const nextState = {};
    CATEGORY_CONFIG.forEach((c) => {
      nextState[c.key] = true;
    });
    nextState.other = true;
    setCollapsedCategories(nextState);
  };

  const handleExpandAll = () => {
    setCollapsedCategories({});
  };

  const handleDeleteAction = async (actionUuid) => {
    const confirm = await confirmationModal(
      "Delete this action?",
      "This will permanently remove the action from this task.",
    );
    if (!confirm.isConfirmed) return;

    try {
      await deleteActionApi({ taskId, actionUuid });
      setTask((prev) => ({
        ...prev,
        actions: (prev?.actions || []).filter((a) => a.uuid !== actionUuid),
      }));
      toast.success("Action deleted.");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to delete action.",
      );
    }
  };

  // import this api
  //deleteTaskApi
  // const handleDeleteTask = async () => {
  //   const confirm = await confirmationModal(
  //     "Delete this entire scraping task?",
  //     "This will permanently delete this task and all its extracted actions.",
  //   );
  //   if (!confirm.isConfirmed) return;

  //   try {
  //     await deleteTaskApi(taskId);
  //     toast.success("Scraping task deleted.");
  //     navigate("/social-media-scraping");
  //   } catch (err) {
  //     toast.error(err?.response?.data?.message || err?.message || "Failed to delete task.");
  //   }
  // };

  return (
    <div
      className="content"
      style={{ maxWidth: "100%", padding: "4px 20px", marginTop: "40px" }}
    >
      <div className="bg-body-emphasis rounded-3 p-4 border mb-4 mt-3">
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <LoadingComponent loading size={15} />
          </div>
        ) : !task ? (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-exclamation-circle fs-1 d-block mb-2" />
            <h5>Task not found</h5>
            <p className="mb-3">
              The requested scraping task could not be loaded or has been
              deleted.
            </p>
            <Link
              to="/social-media-scraping"
              className="btn btn-primary btn-sm"
            >
              Return to Scraping List
            </Link>
          </div>
        ) : (
          <div>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 pb-3 border-bottom">
              <div className="flex-grow-1" style={{ minWidth: 260 }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i
                    className={`bi ${sourceMeta.icon}`}
                    style={{ color: sourceMeta.color, fontSize: 22 }}
                  />
                  <span
                    className={`badge rounded-pill ${badge.cls}`}
                    style={{ fontSize: 12 }}
                  >
                    {badge.label}
                  </span>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span
                      className="badge bg-primary-subtle text-primary-emphasis border border-primary-subtle rounded-pill fw-normal px-2 py-1"
                      style={{ fontSize: 11, whiteSpace: "nowrap" }}
                    >
                      <i className="bi bi-list-check me-1" />
                      {actionCount} Action{actionCount !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <h4
                    className="mb-0 fw-semibold text-body"
                    style={{ wordBreak: "break-all" }}
                  >
                    {task.title}
                  </h4>
                  {task.title?.startsWith("http") && (
                    <a
                      href={task.title}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary text-decoration-none"
                      title="Open URL in new tab"
                    >
                      <i className="bi bi-box-arrow-up-right" />
                    </a>
                  )}
                </div>

                <div
                  className="text-muted mt-2 d-flex align-items-center gap-3"
                  style={{ fontSize: 12 }}
                >
                  <span>
                    <i className="bi bi-calendar3 me-1" />
                    {formatDate(task.createdAt)}
                  </span>
                </div>
              </div>

              {/* <div className="d-flex align-items-center flex-shrink-0">
                <button
                  className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-2 px-3"
                  onClick={handleDeleteTask}
                >
                  <i className="bi bi-trash3" />
                  Delete Task
                </button>
              </div> */}
            </div>

            {task.summary && (
              <div className="mt-3 p-3 bg-body-secondary rounded-3 border">
                <h6
                  className="fw-semibold text-body d-flex align-items-center gap-2 mb-1"
                  style={{ fontSize: 14 }}
                >
                  <i className="bi bi-file-text text-primary" />
                  Analysis Summary
                </h6>
                <p
                  className="mb-0 text-body-secondary"
                  style={{
                    fontSize: 13.5,
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {task.summary}
                </p>
              </div>
            )}

            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-4 mb-3">
              <h5
                className="mb-0 fw-semibold text-body d-flex align-items-center gap-2"
                style={{ fontSize: 16 }}
              >
                <i className="bi bi-collection text-primary" />
                Actions by Category
              </h5>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1 px-3"
                  onClick={handleExpandAll}
                  title="Expand all categories"
                >
                  <i className="bi bi-arrows-expand" />
                  Expand All
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1 px-3"
                  onClick={handleCollapseAll}
                  title="Collapse all categories"
                >
                  <i className="bi bi-arrows-collapse" />
                  Collapse All
                </button>
              </div>
            </div>

            <div className="table-responsive border rounded-3">
              <table className="table table-sm align-top mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-3" style={{ width: 40 }}>
                      #
                    </th>
                    <th>Action</th>
                    <th className="text-center" style={{ width: 110 }}>
                      Replied
                    </th>
                    <th className="text-center" style={{ width: 120 }}>
                      Options
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {sections.map((sec) => {
                    const isCollapsed = Boolean(collapsedCategories[sec.key]);

                    return (
                      <React.Fragment key={sec.key}>
                        {/* Category header row */}
                        <tr
                          role="button"
                          tabIndex={0}
                          style={{
                            backgroundColor: sec.headerBg,
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                          onClick={() => toggleCategory(sec.key)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleCategory(sec.key);
                            }
                          }}
                        >
                          <td colSpan={4} className="py-2 ps-3">
                            <div className="d-flex align-items-center gap-2">
                              <i
                                className={`bi bi-chevron-${isCollapsed ? "right" : "down"} text-secondary`}
                                style={{ fontSize: 13 }}
                              />
                              <i
                                className={`bi ${sec.icon}`}
                                style={{ color: sec.color, fontSize: 18 }}
                              />
                              <span
                                className="fw-semibold text-body"
                                style={{ fontSize: 15 }}
                              >
                                {sec.pluralLabel}
                              </span>
                              <span
                                className={`badge rounded-pill ${sec.badgeClass}`}
                                style={{ fontSize: 12 }}
                              >
                                {sec.items.length}
                              </span>
                            </div>
                          </td>
                        </tr>

                        {/* Rows for this category */}
                        {!isCollapsed &&
                          (sec.items.length === 0 ? (
                            <tr>
                              <td
                                colSpan={4}
                                className="text-center text-muted py-3"
                                style={{ fontSize: 13 }}
                              >
                                <i className="bi bi-inbox me-1" />
                                No {sec.label.toLowerCase()} actions found.
                              </td>
                            </tr>
                          ) : (
                            sec.items.map((action, idx) => (
                              <tr key={action.uuid || idx}>
                                <td className="ps-3 fw-semibold text-body-secondary">
                                  {idx + 1}
                                </td>

                                <td style={{ minWidth: 0 }}>
                                  <div
                                    className="fw-semibold text-body"
                                    style={{
                                      fontSize: 14,
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {action.name || "Untitled Action"}
                                  </div>
                                  {action.description && (
                                    <div
                                      className="text-body-secondary"
                                      style={{
                                        fontSize: 13,
                                        lineHeight: 1.5,
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {action.description}
                                    </div>
                                  )}

                                  {action.originalMessage && (
                                    <div
                                      className="mt-1 p-1 rounded"
                                      style={{
                                        backgroundColor:
                                          "var(--bs-tertiary-bg)",
                                        borderLeft:
                                          "3px solid var(--bs-primary)",
                                        fontSize: 13,
                                        lineHeight: 1.5,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      <div
                                        className="d-flex align-items-center gap-1 mb-1"
                                        style={{
                                          color: "var(--bs-primary)",
                                          fontWeight: 600,
                                        }}
                                      >
                                        <i className="bi bi-chat-left-text" />
                                        Original message
                                      </div>

                                      <div
                                        className="text-body"
                                        style={{ whiteSpace: "pre-wrap" }}
                                      >
                                        {action.originalMessage}
                                      </div>
                                    </div>
                                  )}
                                </td>

                                <td className="text-center align-middle">
                                  {action.isReplied === true ? (
                                    <span className="badge rounded-pill bg-success-subtle text-success-emphasis">
                                      <i className="bi bi-check-circle-fill me-1" />
                                      Yes
                                    </span>
                                  ) : (
                                    <span className="badge rounded-pill bg-danger-subtle text-danger-emphasis">
                                      <i className="bi bi-x-circle-fill me-1" />
                                      No
                                    </span>
                                  )}
                                </td>

                                <td className="text-center align-middle">
                                  {(() => {
                                    const sourceUrl = getActionSourceUrl(
                                      sourceMeta.label,
                                      action.sourceMessageId,
                                      task?.title,
                                    );
                                    return (
                                      <div className="d-inline-flex align-items-center gap-2">
                                        {sourceUrl ? (
                                          <a
                                            href={sourceUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-outline-primary"
                                            title={`Open source on ${sourceMeta.label}`}
                                            aria-label="Open source"
                                          >
                                            <i className="bi bi-box-arrow-up-right" />
                                          </a>
                                        ) : (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            title="No source link available"
                                            aria-label="No source link available"
                                            disabled
                                          >
                                            <i className="bi bi-box-arrow-up-right" />
                                          </button>
                                        )}

                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-danger"
                                          title="Delete action"
                                          aria-label="Delete action"
                                          onClick={() =>
                                            handleDeleteAction(action.uuid)
                                          }
                                        >
                                          <i className="bi bi-trash3" />
                                        </button>
                                      </div>
                                    );
                                  })()}
                                </td>
                              </tr>
                            ))
                          ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SocialMediaScrapingDetail;
